import { StackedAreaChart, type ChartMarker, type ChartSeries } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { InvestmentsPageDto } from "../dto.js";

/**
 * Portfolio value across the window — ONE line, not a stack.
 *
 * `lines` rather than `stacked`, and the distinction is the one 006 drew: a stack composes, so its
 * top is the whole; lines compare, so each is its own value. There is exactly one series here, and a
 * stacked chart of one series draws the same shape while implying a decomposition that does not
 * exist. If per-stock layers are ever added, the choice has to be revisited deliberately rather than
 * inherited.
 *
 * ==========================================================================================
 * THE MARKS ARE WHAT MAKE THE LINE READABLE
 * ==========================================================================================
 *
 * Value moves for two unrelated reasons — the market moved, or the household bought or sold — and
 * once drawn they are the same shape. A step up after a purchase is indistinguishable from a step up
 * after a good month, and the reader will take the first for the second.
 *
 * So a month with a trade gets a dashed rule and a label. Green for money in, red for money out, and
 * the chart takes no view on which is good news.
 *
 * A MONTH WHERE A HOLDING HAD NO PRICE OMITS THAT HOLDING rather than valuing it at zero — the server
 * does that, and the line simply reflects it. The alternative would draw a cliff on the month a stock
 * was added and a matching one when it was first priced, neither of which happened.
 */

export interface PortfolioChartProps {
  page: InvestmentsPageDto;
}

export function PortfolioChart({ page }: PortfolioChartProps): ReactNode {
  const t = useT();
  const markers: ChartMarker[] = page.markers.map((marker) => ({
    at: marker.month,
    label: marker.label,
    direction: marker.direction,
  }));

  const series: ChartSeries[] = [
    {
      id: "portfolio",
      label: t("inv.page.portfolioValue"),
      points: page.chart.map((point) => ({ at: point.month, value: Number(point.value) })),
    },
  ];

  return (
    <StackedAreaChart
      series={series}
      markers={markers}
      variant="lines"
      emptyMessage={t("inv.chart.empty")}
    />
  );
}
